'use client';

import { useState } from 'react';
import type { WerkcurveOutput } from '@/lib/interview/types';

export function hasPlan(w: WerkcurveOutput): boolean {
  return Boolean(w.route?.length || w.risks?.length || w.internal_pitch);
}

export function PlanTab({ werkcurve: w }: { werkcurve: WerkcurveOutput }) {
  const [copied, setCopied] = useState(false);

  const copyPitch = async () => {
    if (!w.internal_pitch) return;
    try {
      await navigator.clipboard.writeText(w.internal_pitch);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked; the text is still there to select
    }
  };

  return (
    <div className="space-y-6">
      {w.route && w.route.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-off-black/10">
          <h2 className="font-display font-light text-2xl text-off-black mb-1">De komende 90 dagen</h2>
          <p className="text-sm text-off-black/50 mb-5">Een route in drie stappen. Klein beginnen, dan pas uitbreiden.</p>
          <ol className="space-y-5">
            {w.route.map((r, i) => (
              <li key={i} className="grid grid-cols-[2rem_1fr] gap-3">
                <span className="h-8 w-8 rounded-full bg-orange-cta/10 text-orange-cta text-sm font-medium flex items-center justify-center">
                  {i + 1}
                </span>
                <div>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <p className="font-medium text-off-black">{r.period}</p>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-off-black/5 text-off-black/60">
                      daarna op {r.level}
                    </span>
                  </div>
                  <p className="text-off-black/70 mt-1">{r.goal}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {w.risks && w.risks.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-off-black/10">
          <h2 className="font-display font-light text-2xl text-off-black mb-1">Wat mis kan gaan</h2>
          <p className="text-sm text-off-black/50 mb-5">En hoe jullie dat voorkomen.</p>
          <ul className="space-y-4">
            {w.risks.map((r, i) => (
              <li key={i} className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                <p className="text-off-black/80">{r.risk}</p>
                <p className="text-sm text-off-black/60 sm:text-base">
                  <span className="text-orange-cta">Zo voorkom je het: </span>
                  {r.prevention}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {w.internal_pitch && (
        <div className="bg-white rounded-2xl p-6 border border-off-black/10">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
            <h2 className="font-display font-light text-2xl text-off-black">Zo leg je het uit aan je MT</h2>
            <button
              type="button"
              onClick={copyPitch}
              className="text-sm underline underline-offset-4 decoration-off-black/20 text-off-black/70 hover:text-off-black no-print"
            >
              {copied ? 'Gekopieerd' : 'Kopieer tekst'}
            </button>
          </div>
          <p className="text-sm text-off-black/50 mb-4">Voor als je dit intern moet verkopen.</p>
          <p className="text-off-black/80 leading-relaxed pl-4 border-l-2 border-orange-cta">{w.internal_pitch}</p>
        </div>
      )}
    </div>
  );
}
